import { z } from 'zod';
import { ADMIN_PASSWORD_MINIMUM, INVITE_VALID_HOURS } from './lists.ts';
import { AUDIT_REASON_MIN_LENGTH } from './audit-reasons.ts';

/**
 * Partner PORTAL ACCESS — provisioning a login for an affiliate or an agent,
 * and the acceptance that turns it into one.
 *
 * Its own file because it is one mechanism across BOTH partner domains, which
 * is the exception to the rule that keeps `affiliate.ts` and `agent.ts` apart.
 * The two domains are structurally independent in every commercial respect —
 * separate tables, separate engines, separate portals — and a shared file is
 * how they start borrowing each other's assumptions. Identity is the one place
 * they genuinely are the same thing: `identity.partner_user` is ONE table with
 * a `partner_kind` discriminator, `identity.partner_invite_token` is ONE table
 * hanging off it, and a token is a credential in both. Two copies of a
 * credential rule is two places for it to drift, and the drift would be silent.
 *
 * WHY THIS EXISTS AT ALL. Filing and approving a partner both worked; nothing
 * in the API ever wrote an `identity.partner_user` row, and nothing ever
 * updated one, so a partner's status never left INVITED and the only partners
 * who could sign in were the ones `seed.ts` created. Every partner onboarded
 * through the console was locked out of its own portal.
 *
 * The shape mirrors the admin invite in `lists.ts` deliberately, down to the
 * constants — see each one.
 */

/** Which portal a login is for. `identity.partner_user.partner_kind`. */
export const partnerKind = z.enum(['AFFILIATE', 'AGENT']);
export type PartnerKind = z.infer<typeof partnerKind>;

/**
 * `identity.partner_user.portal_role` — "Staff see only what you allow. Only
 * you can change payout details."
 *
 * All four are real and all four are enforced: `PARTNER_PORTAL_CAPABILITIES` at
 * the foot of this file says what each reaches, `services/api/src/partner-
 * capability.ts` refuses on it, and both portals draw their role table from it.
 * The console provisions a login at any of them and a partner's own owner can
 * invite staff at the three that are not OWNER.
 *
 * This used to read "the console provisions the OWNER login and nothing else
 * today … a permission model this platform has not built", which was true and
 * is worth keeping a record of: the column carried four values that nothing
 * read, so in practice every partner login saw everything.
 *
 * Stated as an enum rather than left to `varchar(24)` so a request naming
 * anything else is refused by the contract. `packages/db/sql/320` puts the same
 * four behind a CHECK, for the writers that are not routes.
 */
export const partnerPortalRole = z.enum(['OWNER', 'MANAGER', 'RECRUITER', 'BOOKKEEPER']);
export type PartnerPortalRole = z.infer<typeof partnerPortalRole>;

/** `identity.partner_user.status`. INVITED has no password and cannot sign in. */
export const partnerAccountStatus = z.enum(['INVITED', 'ACTIVE', 'SUSPENDED']);
export type PartnerAccountStatus = z.infer<typeof partnerAccountStatus>;

/**
 * ONE number, two realms.
 *
 * "The invite expires in 72 hours" is what the operator console states for an
 * admin invite, and a partner invite that lapsed on a different schedule would
 * be a second rule for the same act. Re-exported rather than re-declared so the
 * two cannot be changed apart.
 */
export const PARTNER_INVITE_VALID_HOURS = INVITE_VALID_HOURS;

/**
 * The same floor as an operator password, for the same mechanical reason:
 * `hashPassword` in @dayon/auth refuses anything shorter, so a lower minimum
 * here would be a form that accepts what the hasher then throws on.
 */
export const PARTNER_PASSWORD_MINIMUM = ADMIN_PASSWORD_MINIMUM;

/* ── Provisioning, from the back office ──────────────────────────────────── */

/**
 * Grant a partner its portal login.
 *
 * The address is carried IN THE REQUEST rather than read off the partner row,
 * because there is no such column: neither `affiliate.affiliate` nor
 * `agent.agent` records an email, and the one place an address means anything
 * is `identity.partner_user`, where a unique index on lower(email) already
 * makes one address one login. Adding a second copy to the partner table would
 * create two sources of truth for a credential's identity, and the copy that
 * was not used for sign-in would be the one an operator read.
 *
 * `portalRole` defaults to OWNER: this control provisions the partner's own
 * login, and the design's sentence about it — "Only you can change payout
 * details" — is a statement about the owner.
 */
export const provisionPortalAccessRequest = z.object({
  email: z.email('Enter a valid email address for the partner.').max(320),
  displayName: z
    .string()
    .trim()
    .min(2, 'Give the person’s name as it should appear in their portal.')
    .max(200),
  portalRole: partnerPortalRole.default('OWNER'),
});
export type ProvisionPortalAccessRequest = z.infer<typeof provisionPortalAccessRequest>;

/**
 * The live invite on a login, or null.
 *
 * `expired` is computed by the SERVER and sent, rather than left to the console
 * to derive from `expiresAt`. A browser clock that is wrong by a day would
 * otherwise draw a live invite as lapsed — and the operator would re-issue over
 * a link the partner is holding.
 */
export const partnerInviteSummary = z.object({
  issuedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  expired: z.boolean(),
});
export type PartnerInviteSummary = z.infer<typeof partnerInviteSummary>;

/**
 * What the console reads to draw the portal-access control.
 *
 * `access` is null when the partner has NO login at all, which is the state
 * every partner onboarded through the console has been stuck in. Null is a real
 * answer and is drawn as one — "no login" and "invited" are different facts,
 * and only one of them is fixed by re-issuing a link.
 */
export const partnerPortalAccessState = z.object({
  partnerKind,
  partnerId: z.string(),
  partnerRef: z.string(),
  partnerName: z.string(),
  /** The partner's OWN status, which is what decides whether it may be granted one. */
  partnerStatus: z.string(),
  /** Whether provisioning would be accepted right now, and why not if it would not. */
  canProvision: z.boolean(),
  refusal: z.string().nullable(),
  access: z
    .object({
      id: z.string(),
      email: z.email(),
      displayName: z.string(),
      portalRole: partnerPortalRole,
      status: partnerAccountStatus,
      lastSignInAt: z.iso.datetime().nullable(),
      invite: partnerInviteSummary.nullable(),
    })
    .nullable(),
});
export type PartnerPortalAccessState = z.infer<typeof partnerPortalAccessState>;

/**
 * What comes back from provisioning, and from re-issuing.
 *
 * `linkPath` is RELATIVE and carries the token in the FRAGMENT, exactly as the
 * admin invite link does: a fragment is never sent to a server, so the token
 * cannot land in an access log, a proxy log or a Referer header on the way to
 * the portal. The browser hands it to the accept screen, which posts it in a
 * body.
 *
 * IT IS BOTH SENT AND SHOWN. `@dayon/mailer` records a PARTNER_INVITE in the
 * same transaction as the token, and the link is returned anyway — whether the
 * message is DELIVERED is per-environment configuration, and an environment
 * running MAIL_OUTBOUND_MODE=SUPPRESS delivers nothing at all.
 *
 * `delivery` is therefore a SHAPE rather than a sentence: it carries the
 * outbox's own status for the row this request wrote, so a client that renders
 * it without reading the console's copy still cannot imply a send that did not
 * happen.
 */
export const partnerInviteLinkResult = z.object({
  partnerUserId: z.string(),
  partnerKind,
  email: z.email(),
  displayName: z.string(),
  portalRole: partnerPortalRole,
  status: partnerAccountStatus,
  /** `/accept-invite#token=…`, relative to the portal named by partnerKind. */
  linkPath: z.string(),
  /**
   * The same path against the partner portal's own origin, from
   * `packages/config`.
   *
   * The admin invite returns only the relative path, which is right there — the
   * console IS the back office, so the link is on its own origin. A partner's
   * portal is a different application on a different origin, and a console that
   * guessed one would hand out a link to the wrong host. The server holds both
   * origins already, so it assembles this.
   */
  linkUrl: z.url(),
  expiresAt: z.iso.datetime(),
  /** Outstanding links this one replaced. Two live links is one more than anyone can account for. */
  supersededLinks: z.number().int().nonnegative(),
  /**
   * What became of the message, from `platform.mail_message` itself.
   *
   * PENDING    — a worker hands it to the provider within the minute.
   * SUPPRESSED — recorded and NOT delivered, because this environment's
   *              MAIL_OUTBOUND_MODE lets nobody receive mail or the address
   *              stands on the suppression list.
   *
   * A shape, not a sentence: a status the console can branch on says the right
   * thing in either case, where a phrase chosen by the server can only ever be
   * right in one of them.
   */
  delivery: z.object({
    messageRef: z.string(),
    status: z.enum(['PENDING', 'SUPPRESSED']),
    to: z.email(),
  }),
});
export type PartnerInviteLinkResult = z.infer<typeof partnerInviteLinkResult>;

/* ── Accepting, from the portal ──────────────────────────────────────────── */

/**
 * A token is 32 random bytes, base64url — 43 characters. The floor is stated
 * generously so a truncated paste is refused as an incomplete link rather than
 * as a lookup miss, which is a different sentence for the person holding it.
 */
const inviteToken = z.string().min(20, 'That invite link is incomplete.').max(200);

/**
 * The body the routes that ACT on one login share: reinstate and re-issue.
 *
 * They took no body at all while a partner had exactly one login. Now that
 * staff logins exist they need to say which, and an optional field with a
 * documented default is better than two routes — the alternative was
 * `/portal-access/reinstate` and `/portal-access/logins/:id/reinstate` saying
 * the same thing twice.
 */
export const targetPortalLoginRequest = z.object({
  partnerUserId: z.string().min(1).max(64).optional(),
});

/**
 * Reinstating a suspended login, which needs a reason — PAGCOR item 41.
 *
 * Giving somebody their access back is an alteration to a record that is not the
 * operator's own, and it is the half of a suspension that nobody writes down: the
 * suspension carries its reason, and the reinstatement used to carry nothing, so
 * the trail said why access was taken away and never why it was returned.
 *
 * Re-issuing an invite is left on `targetPortalLoginRequest`: that sends a link
 * again rather than changing the account's state.
 *
 * Ten characters, matching `rejectWithdrawalRequest` and the floor in sql/996.
 */
export const reinstatePortalAccessRequest = targetPortalLoginRequest.extend({
  reason: z
    .string()
    .trim()
    .min(10, 'Say why this access is being restored — this is what the trail will show.')
    .max(500),
});
export type ReinstatePortalAccessRequest = z.infer<typeof reinstatePortalAccessRequest>;
export type TargetPortalLoginRequest = z.infer<typeof targetPortalLoginRequest>;

export const partnerInvitePreviewRequest = z.object({ token: inviteToken });
export type PartnerInvitePreviewRequest = z.infer<typeof partnerInvitePreviewRequest>;

export const partnerInvitePreviewResult = z.object({
  partnerKind,
  email: z.email(),
  displayName: z.string(),
  /** "AF-1201 · Sunrise Reviews" — so the person can tell whose portal this is. */
  partnerRef: z.string(),
  partnerName: z.string(),
  portalRole: partnerPortalRole,
  expiresAt: z.iso.datetime(),
  passwordMinimum: z.number().int().positive(),
  /**
   * Whether the portal will ask for a second factor after this.
   *
   * Sent, and FALSE, because it is the honest answer today: neither partner
   * sign-in route reads `identity.partner_totp_secret` or issues a two-factor
   * challenge — both mint a session with `mfa: false` straight after the
   * password. An acceptance screen that enrolled an authenticator would be
   * asking for a factor nothing checks, and the partner would reasonably
   * believe their account was protected by it. The field exists so that the day
   * partner sign-in grows a factor, the screen learns about it from the server
   * rather than from a second deploy.
   */
  twoFactorRequired: z.literal(false),
});
export type PartnerInvitePreviewResult = z.infer<typeof partnerInvitePreviewResult>;

/**
 * Set the password and become ACTIVE.
 *
 * NO `code` FIELD, and that is a decision rather than an omission — see
 * `twoFactorRequired` above. The admin acceptance takes one because the admin
 * sign-in demands one; the partner sign-in does not, and a gate the sign-in
 * will not honour is theatre that locks people out for nothing.
 */
export const acceptPartnerInviteRequest = z.object({
  token: inviteToken,
  password: z
    .string()
    .min(
      PARTNER_PASSWORD_MINIMUM,
      `Choose at least ${PARTNER_PASSWORD_MINIMUM} characters — this account sees what you are paid.`,
    )
    .max(200),
});
export type AcceptPartnerInviteRequest = z.infer<typeof acceptPartnerInviteRequest>;

export const acceptPartnerInviteResult = z.object({
  partnerKind,
  email: z.email(),
  displayName: z.string(),
  portalRole: partnerPortalRole,
  status: z.literal('ACTIVE'),
  /**
   * No session is minted here. Accepting proves you hold the link; signing in
   * is a separate act with its own trail, and minting a session here would skip
   * the sign-in record that trail is made of.
   */
  signInRequired: z.literal(true),
});
export type AcceptPartnerInviteResult = z.infer<typeof acceptPartnerInviteResult>;

/* ── Ending and restoring access ──────────────────────────────────────────── */

/**
 * Suspension carries a reason, and the reason is required.
 *
 * `identity.partner_user` records that access ended and when; only this says
 * why. An operator reading the audit trail six months later is usually trying
 * to answer whether a partner was cut off for a contract ending or for
 * suspected fraud, and those two lead to opposite next steps. A free-text box
 * nobody must fill is a box nobody fills.
 */
export const suspendPortalAccessRequest = z.object({
  reason: z
    .string()
    .trim()
    /* Ten, not eight. The handler writes this sentence into the audit row
       unchanged, and the database's floor is ten — so eight here meant a
       nine-character reason was accepted, stored, refused, and reported to the
       operator as "That change is not allowed by the platform rules". */
    .min(
      AUDIT_REASON_MIN_LENGTH,
      'Say why this login is being suspended — the audit trail keeps no other record of it. ' +
        `At least ${AUDIT_REASON_MIN_LENGTH} characters.`,
    )
    .max(500),
  /**
   * WHICH login, when the partner has more than one.
   *
   * Omitting it means the OWNER's, which is what every one of these routes
   * meant when a partner could only have one login. A staff login is named
   * explicitly, and the id is ANDed onto a lookup already scoped by the partner
   * in the path and the operator's brand scope — so an id belonging to another
   * partner matches nothing and is reported as absent.
   */
  partnerUserId: z.string().min(1).max(64).optional(),
});
export type SuspendPortalAccessRequest = z.infer<typeof suspendPortalAccessRequest>;

export const suspendPortalAccessResult = z.object({
  partnerUserId: z.string(),
  email: z.email(),
  displayName: z.string(),
  status: z.literal('SUSPENDED'),
  /** Live acceptance links die with the login; the count is shown to the operator. */
  revokedInviteLinks: z.number().int().nonnegative(),
});
export type SuspendPortalAccessResult = z.infer<typeof suspendPortalAccessResult>;

export const reinstatePortalAccessResult = z.object({
  partnerUserId: z.string(),
  email: z.email(),
  displayName: z.string(),
  /**
   * ACTIVE only if a password was set before the suspension. A partner
   * suspended before accepting their invite goes back to INVITED, because
   * marking it ACTIVE would advertise a login every sign-in refuses.
   */
  status: z.enum(['ACTIVE', 'INVITED']),
  /** INVITED means the operator still has to issue a fresh link. */
  inviteLinkRequired: z.boolean(),
});
export type ReinstatePortalAccessResult = z.infer<typeof reinstatePortalAccessResult>;

/* ══════════════════════════════════════════════════════════════════════════
   WHO INSIDE A PARTNER MAY REACH WHAT

   `identity.partner_user.portal_role` has carried four values since the schema
   was written — OWNER · MANAGER · RECRUITER · BOOKKEEPER — and until now
   NOTHING read it except two hand-written checks (`GET /api/affiliate/staff`
   and `POST /api/agent/downline/split`, both spelling out `['OWNER',
   'MANAGER']` inline). Every other partner-portal route consulted nothing at
   all, so in practice every partner login saw everything: statements, payouts,
   downline, tickets. The portal drew a four-row table of what each role may do
   and the server enforced none of it.

   This map is that table, made real. It lives in contracts rather than in the
   API because BOTH ends have to agree: the server refuses on it, and the
   portals render the role matrix from it. A screen that described the model in
   its own prose is a screen that drifts from the model the moment one of them
   changes, and "what your staff can see" is not a claim to leave to two copies.

   ── HOW IT WAS DECIDED ─────────────────────────────────────────────────────

   Conservatively, and from what the product already said out loud. The four
   rows below are the design's own sentences, which both portals were already
   printing:

     Owner       "Everything a Manager can do, plus the exclusive right to
                  change payout details."
     Manager     "Both earnings and recruiting. Still cannot change where the
                  money is paid."
     Bookkeeper  "May see earnings, statements and payouts. May not recruit."
     Recruiter   "Tracking links and creatives. May not see earnings,
                  statements, payouts or this list."

   No fifth role was invented, and no capability was granted that those four
   sentences do not already imply. Where a sentence was silent the answer is NO,
   because the failure of guessing wrong in that direction is a staff member who
   has to ask for access, and in the other direction it is a Recruiter reading
   what the partner is paid.

   ── THE ONE PLACE THAT NEEDED A JUDGEMENT ──────────────────────────────────

   THE PLAYER / DOWNLINE LIST IS NOT SHOWN TO A RECRUITER, even though a
   Recruiter recruits. `GET /api/agent/players` returns deposits and GGR per
   player and `GET /api/affiliate/players` returns deposits, net GGR and a
   derived commission — every row of both lists is a revenue figure. A role that
   "may not see earnings" cannot be handed a screen made of them, and redacting
   half a table would leave the portal claiming to show a downline while showing
   something else. So a Recruiter mints and shares links, and asks the Owner or
   a Manager for the numbers. This is stated on the staff screen in those words
   rather than left to be discovered.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The closed set of things a partner-portal login can be allowed to reach.
 *
 * Deliberately coarse — ten capabilities over roughly twenty routes — because
 * a capability the product cannot describe in one sentence is a capability
 * nobody can decide about. VIEW and MANAGE are split only where the same screen
 * carries a read that is ordinary and a write that moves money or hands out a
 * credential.
 */
export const partnerPortalCapability = z.enum([
  /** The earnings dashboard, and the affiliate's traffic funnel — which carries EPC. */
  'EARNINGS',
  /** Settled commission periods: what each closed period paid. */
  'STATEMENTS',
  /** The payout destination the platform holds, and the payouts it has made. */
  'PAYOUTS_VIEW',
  /** Change payout details, or ask for an early payout. "Only you can change payout details." */
  'PAYOUTS_MANAGE',
  /** The players or sub-agents this partner recruited. Every row carries revenue. */
  'DOWNLINE_VIEW',
  /** Change a sub-agent's commission split. */
  'DOWNLINE_MANAGE',
  /** Tracking links, referral links and creatives. */
  'LINKS',
  /** The ticket thread with the platform team. */
  'SUPPORT',
  /** The office list — who in this partner can sign in. */
  'STAFF_VIEW',
  /** Invite staff, and end a colleague's access. Handing out a credential. */
  'STAFF_MANAGE',
]);
export type PartnerPortalCapability = z.infer<typeof partnerPortalCapability>;

/**
 * What each role holds. THE map — the server refuses on it and the portals draw
 * their role matrix from it.
 *
 * OWNER is written out in full rather than derived from the others. A role
 * defined as "everything" is a role that silently acquires each new capability
 * the day it is added, which is the correct answer here and would still be the
 * wrong way to write it down: the map is read by people deciding whether a
 * change is safe, and "everything" tells them nothing about what that is.
 */
export const PARTNER_PORTAL_CAPABILITIES: Readonly<
  Record<PartnerPortalRole, readonly PartnerPortalCapability[]>
> = {
  OWNER: [
    'EARNINGS',
    'STATEMENTS',
    'PAYOUTS_VIEW',
    'PAYOUTS_MANAGE',
    'DOWNLINE_VIEW',
    'DOWNLINE_MANAGE',
    'LINKS',
    'SUPPORT',
    'STAFF_VIEW',
    'STAFF_MANAGE',
  ],
  /* Everything the Owner has except the two that are the Owner's alone: where
     the money goes, and who else gets a login. */
  MANAGER: [
    'EARNINGS',
    'STATEMENTS',
    'PAYOUTS_VIEW',
    'DOWNLINE_VIEW',
    'DOWNLINE_MANAGE',
    'LINKS',
    'SUPPORT',
    'STAFF_VIEW',
  ],
  /* The money, and only the money. No links, no downline, no office list — and
     no ability to move the money either, only to read what moved. */
  BOOKKEEPER: ['EARNINGS', 'STATEMENTS', 'PAYOUTS_VIEW', 'SUPPORT'],
  /* Links and creatives. Not the player list, because every row of it is a
     revenue figure — see the note above this map. */
  RECRUITER: ['LINKS', 'SUPPORT'],
};

/** Whether a role holds a capability. The one predicate; nothing re-derives it. */
export const partnerRoleHas = (
  role: PartnerPortalRole,
  capability: PartnerPortalCapability,
): boolean => PARTNER_PORTAL_CAPABILITIES[role].includes(capability);

/**
 * What to call a capability when refusing one, and when drawing the matrix.
 *
 * In the partner's own words, not the platform's. "DOWNLINE_VIEW" is a column
 * name; "your players and sub-agents" is what the person on the other end of
 * the refusal was trying to open.
 */
export const PARTNER_PORTAL_CAPABILITY_LABEL: Readonly<
  Record<PartnerPortalCapability, string>
> = {
  EARNINGS: 'earnings',
  STATEMENTS: 'statements',
  PAYOUTS_VIEW: 'payouts',
  PAYOUTS_MANAGE: 'payout details',
  DOWNLINE_VIEW: 'your players and sub-agents',
  DOWNLINE_MANAGE: 'sub-agent commission splits',
  LINKS: 'tracking links and creatives',
  SUPPORT: 'support tickets',
  STAFF_VIEW: 'the staff list',
  STAFF_MANAGE: 'staff accounts',
};

/** In the order the portals print them: most reach first. */
export const PARTNER_PORTAL_ROLE_ORDER: readonly PartnerPortalRole[] = [
  'OWNER',
  'MANAGER',
  'BOOKKEEPER',
  'RECRUITER',
];

/**
 * The one-line description of each role, shared by both portals and the
 * console so the three cannot describe the same column differently.
 */
export const PARTNER_PORTAL_ROLE_NOTE: Readonly<Record<PartnerPortalRole, string>> = {
  OWNER:
    'Everything a Manager can do, plus the two things that are the owner’s alone: changing ' +
    'where the money is paid, and giving somebody else a login.',
  MANAGER:
    'Earnings, statements, the downline and the office list. Cannot change payout details and ' +
    'cannot invite staff.',
  BOOKKEEPER:
    'Earnings, statements and payouts. No tracking links, no player list and no office list — ' +
    'and no way to move money or change where it goes.',
  RECRUITER:
    'Tracking links and creatives. No earnings, no statements, no payouts — and not the player ' +
    'list either, because every row of it carries deposits and GGR.',
};

/* ══════════════════════════════════════════════════════════════════════════
   RESETTING A CREDENTIAL

   The other half of what `modules/partner-access` said it deliberately did not
   do. Re-issuing an invite is refused on an ACTIVE login on purpose, because a
   fresh link on a live account is a credential RESET — a different decision,
   with a different blast radius, and one that ends whatever session the partner
   is holding.

   So it is its own act, on its own route, against its own table
   (`identity.partner_password_reset_token`), with its own audit action. And the
   thing that makes it different from an invite is stated in the RESPONSE rather
   than left to the console to know: `sessionsEnded` and `oldPasswordStopsNow`
   are sent, because an operator resetting a partner mid-afternoon is cutting
   them off and should be told so by the server that did it.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * An operator resetting a partner's password says why, exactly as suspension
 * does, and for the same reason: the row records that a credential was reset
 * and when, and nothing else records why. "They rang up and could not sign in"
 * and "their laptop was stolen" lead to opposite next steps.
 */
export const resetPartnerCredentialRequest = z.object({
  reason: z
    .string()
    .trim()
    /* Ten, for the reason `suspendPortalAccessRequest` gives above. */
    .min(
      AUDIT_REASON_MIN_LENGTH,
      'Say why this credential is being reset — it ends their live sessions, and the audit ' +
        `trail keeps no other record of the reason. At least ${AUDIT_REASON_MIN_LENGTH} characters.`,
    )
    .max(500),
  /**
   * WHICH login, when the partner has more than one.
   *
   * Optional, and omitting it means the OWNER login — which is what every
   * partner had when this module was written and what the console still opens
   * by default. A staff login is targeted explicitly, and the id is checked
   * against the partner in the path before anything is done with it, so a
   * borrowed id from another partner is refused rather than acted on.
   */
  partnerUserId: z.string().min(1).max(64).optional(),
});
export type ResetPartnerCredentialRequest = z.infer<typeof resetPartnerCredentialRequest>;

export const resetPartnerCredentialResult = z.object({
  partnerUserId: z.string(),
  partnerKind,
  email: z.email(),
  displayName: z.string(),
  portalRole: partnerPortalRole,
  /** `/reset-password#token=…`, relative to the portal named by partnerKind. */
  linkPath: z.string(),
  linkUrl: z.url(),
  expiresAt: z.iso.datetime(),
  /** Outstanding invite links and reset links this one killed. */
  supersededLinks: z.number().int().nonnegative(),
  /**
   * TRUE, always, and sent rather than assumed by the screen.
   *
   * A reset bumps `token_version`, and the session plugin re-reads it on every
   * request — so a partner with the portal open is refused on their next click,
   * not at their token's expiry. The console states this because an operator
   * who thinks they are only sending an email is making a decision they did not
   * know they were making.
   */
  sessionsEnded: z.literal(true),
  /**
   * TRUE, always. `password_reset_required_at` is set in the same statement, and
   * both sign-in routes refuse while it stands. The partner cannot get back in
   * with the password they know — only with the link.
   */
  oldPasswordStopsNow: z.literal(true),
  delivery: z.object({
    messageRef: z.string(),
    status: z.enum(['PENDING', 'SUPPRESSED']),
    to: z.email(),
  }),
});
export type ResetPartnerCredentialResult = z.infer<typeof resetPartnerCredentialResult>;

/* ── "I forgot my password", from the portal ─────────────────────────────── */

/**
 * The unauthenticated request. An address, and nothing else.
 *
 * NO partner kind in the body: the route is per-portal
 * (`POST /api/affiliate/password-reset` and `POST /api/agent/password-reset`),
 * so the realm comes from which application the person is standing in. A kind
 * in the body would let the affiliate portal's form be used to probe which
 * addresses hold AGENT logins.
 */
export const partnerForgotPasswordRequest = z.object({
  email: z.email('Enter the email address you sign in with.').max(320),
});
export type PartnerForgotPasswordRequest = z.infer<typeof partnerForgotPasswordRequest>;

/**
 * THE SAME ANSWER, WHATEVER HAPPENED.
 *
 * One literal field with one literal value, so there is nothing in the shape
 * for a caller to read a difference out of. A known address, an unknown
 * address, a suspended login, an address that belongs to the OTHER portal, and
 * one that has never accepted its invite all return this — byte for byte — and
 * the route floors its own duration so the four cannot be told apart by a
 * stopwatch either.
 *
 * The message is deliberately conditional ("if that address has a login") and
 * says what to do next in both worlds. A page that said "check your email"
 * flatly would be a lie to the person who typed the wrong address, and they are
 * the person most in need of being told to look again.
 */
export const partnerForgotPasswordResult = z.object({
  accepted: z.literal(true),
  message: z.literal(
    'If that address has a login on this portal, a reset link is on its way to it. The link ' +
      'works once and expires. If nothing arrives in a few minutes, check the address you typed ' +
      'and then contact your account manager — we cannot tell you whether an address is ' +
      'registered.',
  ),
});
export type PartnerForgotPasswordResult = z.infer<typeof partnerForgotPasswordResult>;

/** What the reset screen needs to draw itself, from the token alone. */
export const partnerResetPreviewRequest = z.object({ token: inviteToken });
export type PartnerResetPreviewRequest = z.infer<typeof partnerResetPreviewRequest>;

export const partnerResetPreviewResult = z.object({
  partnerKind,
  email: z.email(),
  displayName: z.string(),
  partnerRef: z.string(),
  partnerName: z.string(),
  portalRole: partnerPortalRole,
  expiresAt: z.iso.datetime(),
  passwordMinimum: z.number().int().positive(),
  /**
   * Who asked for this reset — and it is shown.
   *
   * A partner who did not ask for a reset and is looking at a link for one
   * needs to know whether the platform initiated it. OPERATOR means somebody at
   * the platform decided; PARTNER means this link came from the portal's own
   * forgot-password form. Telling them costs nothing an attacker holding the
   * token could not already infer, and withholding it is how a
   * platform-initiated lockout reads as a phishing attempt.
   */
  requestedBy: z.enum(['OPERATOR', 'PARTNER']),
  /** False today, for the reason `partnerInvitePreviewResult` gives at length. */
  twoFactorRequired: z.literal(false),
});
export type PartnerResetPreviewResult = z.infer<typeof partnerResetPreviewResult>;

export const completePartnerResetRequest = z.object({
  token: inviteToken,
  password: z
    .string()
    .min(
      PARTNER_PASSWORD_MINIMUM,
      `Choose at least ${PARTNER_PASSWORD_MINIMUM} characters — this account sees what you are paid.`,
    )
    .max(200),
});
export type CompletePartnerResetRequest = z.infer<typeof completePartnerResetRequest>;

export const completePartnerResetResult = z.object({
  partnerKind,
  email: z.email(),
  displayName: z.string(),
  portalRole: partnerPortalRole,
  /**
   * No session is minted here either — see `acceptPartnerInviteResult`. Setting
   * a password proves you hold the link; signing in is a separate act with its
   * own record, and minting a session here would skip it.
   */
  signInRequired: z.literal(true),
});
export type CompletePartnerResetResult = z.infer<typeof completePartnerResetResult>;

/* ── Staff, invited by the partner's own owner ───────────────────────────── */

/**
 * An OWNER inviting somebody in their own office.
 *
 * `role` cannot be OWNER: a partner has exactly one, `partner_user_one_owner_idx`
 * enforces it in the database, and "invite" is not the control for handing over
 * a business. The three that remain are the three the portal has always listed.
 */
export const invitePartnerStaffRequest = z.object({
  email: z.email('Enter a work email address for them.').max(320),
  displayName: z
    .string()
    .trim()
    .min(2, 'Give their name as it should appear in the portal.')
    .max(200),
  role: z.enum(['MANAGER', 'RECRUITER', 'BOOKKEEPER'], {
    error: 'Choose Manager, Recruiter or Bookkeeper. There is one owner, and it is you.',
  }),
});
export type InvitePartnerStaffRequest = z.infer<typeof invitePartnerStaffRequest>;

/**
 * What the owner is told.
 *
 * NO LINK AND NO TOKEN, and that is the difference between this and the
 * console's version of the same act. The back office shows the operator the
 * link because the operator may have to hand it over by mouth in an environment
 * where mail is suppressed. A partner cannot be given that: the link sets a
 * password on an account inside their office, and an owner holding it could set
 * the password themselves and sign in as their own bookkeeper — with the audit
 * trail recording the bookkeeper. So the message is the only copy, and
 * `delivery` says honestly whether one will actually be sent.
 */
export const invitePartnerStaffResult = z.object({
  partnerUserId: z.string(),
  email: z.email(),
  displayName: z.string(),
  role: partnerPortalRole,
  expiresAt: z.iso.datetime(),
  delivery: z.object({
    messageRef: z.string(),
    status: z.enum(['PENDING', 'SUPPRESSED']),
    to: z.email(),
  }),
});
export type InvitePartnerStaffResult = z.infer<typeof invitePartnerStaffResult>;

/* ── Where a partner is paid ─────────────────────────────────────────────── */

/**
 * The rails a partner may be paid on.
 *
 * A CLOSED SET IN CODE, because nothing in the database holds one.
 * `payment.payment_method` is the catalogue of rails a PLAYER may deposit
 * through — a different question, owned by a different domain, and one
 * `dayon_partner` cannot read at all. So the choice a partner is offered is a
 * decision this contract makes, and it makes exactly the three the portal has
 * always shown rather than inventing rails nobody agreed to carry.
 *
 * Served by the API and rendered from what it serves, never printed from a
 * second copy in the portal: the list the screen offers and the list the server
 * accepts are then the same list, which is the whole reason it is here and not
 * in a component.
 *
 * A row may already hold a method that is NOT in this set — an operator wrote
 * it, or the set changed since. Reads return whatever is stored, verbatim;
 * only a CHANGE is held to this list. A partner shown a blank destination
 * because their rail was retired would be a partner told the platform holds
 * nothing for them, which is a different and much worse statement.
 */
export const PARTNER_PAYOUT_METHODS = ['GCash', 'BPI', 'UnionBank'] as const;

export const partnerPayoutMethod = z.enum(PARTNER_PAYOUT_METHODS, {
  error: 'Choose GCash, BPI or UnionBank.',
});
export type PartnerPayoutMethod = z.infer<typeof partnerPayoutMethod>;

/**
 * "Changing the account pauses payouts for 24 hours as a security hold."
 *
 * The number the portal prints and the number the server writes into the hold
 * are this one. There is no `platform.setting` behind it — the agent policy
 * view exposes a closed list of two keys and this is not one of them — so it is
 * a constant, stated once, and SERVED to the screen rather than copied into it.
 */
export const PAYOUT_CHANGE_HOLD_HOURS = 24;

/**
 * The destination account, as a partner types it.
 *
 * Deliberately the same RULE as `railAccountNumber` in `player-money.ts` —
 * digits with spaces, `+` and `-`, at least four digits, no longer than any
 * account number this platform accepts — because it is the same kind of thing
 * being typed into the same country's rails. The copy differs because the
 * reader does: one is a player naming where their own withdrawal goes, the
 * other is a business naming where a week of commission goes.
 *
 * And the same discipline follows it: what is typed here is masked to its last
 * four digits and the rest is DISCARDED before anything is written. The
 * platform's account vault holds PLAYERS' registered payout accounts only; a
 * partner's destination is not routed through it, so a full account number would
 * sit in a plain column that support can read, back up and replicate.
 */
export const partnerPayoutAccountNumber = z
  .string()
  .trim()
  .min(4, 'Enter the account or mobile number your commission should be paid to.')
  .max(34, 'That is longer than any account number this platform accepts.')
  .regex(/^[0-9 +-]+$/, 'Use digits only — spaces, + and - are allowed.')
  .refine((v) => v.replace(/\D/g, '').length >= 4, {
    message: 'Enter at least four digits.',
  });

/**
 * A partner changing where it is paid.
 *
 * `accountName` IS ACCEPTED AND CANNOT CHANGE ANYTHING. The account has to be
 * in the partner's own name and match the identity recorded at KYB, so the name
 * is not the partner's to edit — and a request that carries a different one is
 * REFUSED by name rather than silently ignored. Dropping it quietly would let a
 * caller believe the platform had accepted a rename it never applied, which is
 * the worst possible answer for the one field that ties a bank account to a
 * verified identity.
 *
 * There is no partner id here, and there never will be. The partner is the one
 * whose session cookie was presented; a body that named an id would be a body
 * an attacker could edit.
 */
export const changePayoutAccountRequest = z.object({
  method: partnerPayoutMethod,
  accountNumber: partnerPayoutAccountNumber,
  accountName: z.string().trim().max(200).optional(),
});
export type ChangePayoutAccountRequest = z.infer<typeof changePayoutAccountRequest>;
